'use client';

/** Sunucu verisi + istemci fetch kuralları: `frontend/docs/faq-client-fetch-policy.md` */

import Accordion from "@/components/ui/Accordion";
import {motion} from "framer-motion";
import React, {useEffect, useMemo, useState} from "react";
import {usePathname} from "next/navigation";
import {headerVariants, pageVariants} from "@/lib/animations";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { FaqEntry } from "@/types/faq";
import type { PageUrlMap } from "@/types/blog";
import { stripDefaultLanguagePrefix } from "@/lib/blog-transformers";
import { debugLog, debugWarn, logError } from "@/lib/utils/debug";

interface FAQItem {
    id: string;
    title: string;
    content: string;
}

interface FaqsProps {
    generalFaqs?: FaqEntry[];
    /**
     * FAQ ID'leri (client-side fetch için)
     * Eğer categories ve generalFaqs props olarak sağlanmışsa, bu prop kullanılmaz
     */
    faqIds?: number[];
    /**
     * Kategori slug'ı (URL'den alınır veya prop olarak geçirilir)
     */
    categorySlug?: string;
    language?: string;
    languageCode?: string;
    languageId?: number;
    /**
     * Başlık (GrapeJS editöründen düzenlenebilir)
     */
    title?: string;
    /**
     * Props'tan veri gelmezse client-side'da veri çekmeyi etkinleştir
     * GrapeJS preview için kullanışlıdır
     */
    enableClientFetch?: boolean;
    /**
     * Page URLs map (dinamik slug çıkarma için)
     */
    pageUrls?: PageUrlMap;
    /**
     * Default language (dinamik slug çıkarma için)
     */
    defaultLanguage?: string;
    __pageContext?: {
        language?: string;
        languageCode?: string;
        languageId?: number;
        pageUrls?: PageUrlMap;
        defaultLanguage?: string;
    };
}

// Kategorileri kaldırdık - sadece düz FAQ listesi
const buildFaqList = (generalFaqs: FaqEntry[]): FAQItem[] => {
    return generalFaqs.map((faq) => ({
        id: `faq-${faq.id}`,
        title: faq.question,
        content: faq.answer,
    }));
};

export default function FaqCatPage({
    generalFaqs: initialGeneralFaqs = [],
    faqIds,
    categorySlug: propCategorySlug,
    language,
    languageCode,
    languageId,
    title = 'Frequently Asked Question',
    enableClientFetch = true, // Default true - URL'den slug alındığında otomatik fetch yapılacak
    pageUrls,
    defaultLanguage = 'en',
    __pageContext,
}: FaqsProps){
    const pathname = usePathname();
    const [openAccordion, setOpenAccordion] = useState<string | null>(null);
    const [generalFaqs, setGeneralFaqs] = useState<FaqEntry[]>(initialGeneralFaqs);
    const [fetchedFaqs, setFetchedFaqs] = useState<FAQItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Language variables için state
    const [variables, setVariables] = useState<Record<string, string>>({});

    // Memoize resolved values to prevent unnecessary re-renders
    const resolvedLanguageCode = useMemo(
        () =>
            language ||
            languageCode ||
            __pageContext?.language ||
            __pageContext?.languageCode ||
            undefined,
        [language, languageCode, __pageContext?.language, __pageContext?.languageCode]
    );

    const resolvedLanguageId = useMemo(
        () => languageId || __pageContext?.languageId || undefined,
        [languageId, __pageContext?.languageId]
    );

    const resolvedPageUrls = useMemo(
        () => pageUrls || __pageContext?.pageUrls,
        [pageUrls, __pageContext?.pageUrls]
    );
    const resolvedDefaultLanguage = useMemo(
        () => {
            const lang = defaultLanguage || __pageContext?.defaultLanguage;
            if (!lang) {
                if (process.env.NODE_ENV === 'development') {
                    console.error('FaqCatPage: defaultLanguage prop is required');
                }
                return '';
            }
            return lang;
        },
        [defaultLanguage, __pageContext?.defaultLanguage]
    );

    // URL'den kategori slug'ını çıkar
    // Öncelik: propCategorySlug (server-side'dan gelir) > pathname (client-side fallback, dinamik)
    const categorySlugFromUrl = useMemo(() => {
        // Önce prop'tan gelen slug'ı kullan (server-side'dan gelir, daha güvenilir)
        if (propCategorySlug) {
            return propCategorySlug;
        }
        
        // Fallback: Client-side'da pathname'den dinamik olarak çıkar
        if (!pathname || typeof window === 'undefined') {
            return undefined;
        }
        
        // page-urls'den faq-cat key'inin URL'lerini dinamik olarak kullanarak slug çıkar
        if (resolvedPageUrls && resolvedPageUrls['faq-cat']) {
            const faqCatUrls = resolvedPageUrls['faq-cat'];
            const pathParts = pathname.split('/').filter(Boolean);
            
            // Tüm diller için normalize edilmiş URL path'lerini topla
            const normalizedPaths: string[][] = [];
            for (const [lang, url] of Object.entries(faqCatUrls)) {
                if (url) {
                    // URL'yi normalize et: default language prefix'ini kaldır
                    const normalized = stripDefaultLanguagePrefix(url, resolvedDefaultLanguage);
                    // Path'i segment'lere çevir
                    const segments = normalized.split('/').filter(Boolean);
                    if (segments.length > 0) {
                        normalizedPaths.push(segments);
                    }
                }
            }
            
            // pathParts'in bu normalize edilmiş path'lerden biriyle başlayıp başlamadığını kontrol et
            for (const pathSegments of normalizedPaths) {
                // pathParts'in bu path ile başlayıp başlamadığını kontrol et
                if (pathParts.length >= pathSegments.length) {
                    let matches = true;
                    for (let i = 0; i < pathSegments.length; i++) {
                        if (pathParts[i] !== pathSegments[i]) {
                            matches = false;
                            break;
                        }
                    }
                    
                    // Eşleşme varsa, path'ten sonraki segment'i slug olarak al
                    if (matches && pathParts.length > pathSegments.length) {
                        return pathParts[pathSegments.length];
                    }
                }
            }
        }
        
        // Son fallback: page-urls gelmese bile, URL yapısından slug çıkar
        // pathParts = ["faqs", "payment-security-en"] -> slug = "payment-security-en"
        // veya ["en", "faqs", "payment-security-en"] -> slug = "payment-security-en"
        const pathParts = pathname.split('/').filter(Boolean);
        if (pathParts.length >= 2) {
            // Language prefix kontrolü: İlk eleman 2 karakter ise muhtemelen language code
            const possibleLang = pathParts[0];
            const isLanguagePrefix = possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang);
            
            if (isLanguagePrefix && pathParts.length >= 3) {
                // Language prefix varsa: ["en", "faqs", "slug"] -> slug = "slug" (3. eleman)
                return pathParts[2];
            } else if (!isLanguagePrefix) {
                // Language prefix yoksa: ["faqs", "slug"] -> slug = "slug" (2. eleman)
                return pathParts[1];
            }
        }
        
        return undefined;
    }, [pathname, propCategorySlug, resolvedPageUrls, resolvedDefaultLanguage]);

    const selectedFaqIds = useMemo(
        () => (Array.isArray(faqIds) ? faqIds.filter((id): id is number => Number.isInteger(id)) : []),
        [faqIds],
    );
    
    // Props'tan veri geldiyse client-side fetch yapma
    const hasInitialData = useMemo(() => initialGeneralFaqs.length > 0, [initialGeneralFaqs.length]);
    // Kategori slug'ı varsa her zaman fetch yap (URL'den geldiği için)
    // enableClientFetch true ise ve initial data yoksa da fetch yap
    const shouldFetch = Boolean(categorySlugFromUrl) || (enableClientFetch && !hasInitialData);
    
    // Debug log (sadece bir kez çalışsın - useMemo ile kontrol)
    const debugInfo = useMemo(() => {
        if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') {
            return null;
        }
        
        const faqCatUrls = resolvedPageUrls?.['faq-cat'];
        const normalizedPaths: string[][] = [];
        if (faqCatUrls) {
            for (const [lang, url] of Object.entries(faqCatUrls)) {
                if (url) {
                    const normalized = stripDefaultLanguagePrefix(url, resolvedDefaultLanguage);
                    const segments = normalized.split('/').filter(Boolean);
                    if (segments.length > 0) {
                        normalizedPaths.push(segments);
                    }
                }
            }
        }
        
        return {
            categorySlugFromUrl,
            propCategorySlug,
            shouldFetch,
            hasInitialData,
            enableClientFetch,
            pathname,
            resolvedPageUrls: resolvedPageUrls ? 'exists' : 'undefined',
            resolvedDefaultLanguage,
            faqCatUrls: faqCatUrls ? Object.entries(faqCatUrls).map(([k, v]) => `${k}: ${v}`) : 'none',
            normalizedPaths,
            pathParts: pathname ? pathname.split('/').filter(Boolean) : [],
        };
    }, [categorySlugFromUrl, propCategorySlug, shouldFetch, hasInitialData, enableClientFetch, pathname, resolvedPageUrls, resolvedDefaultLanguage]);
    // Helper function to get variable with fallback
    const getVariable = (key: string, fallback: string): string => {
        return variables[key] || fallback;
    };

    const handleToggle = (id: string) => {
        setOpenAccordion(prev => prev === id ? null : id);
    };

    // Language variables - Context'ten al (duplicate fetch'i önlemek için)
    const { variables: contextVariables } = useLanguageVariables();
    
    // Context'ten gelen variables'ı kullan, yoksa boş object
    useEffect(() => {
        if (contextVariables && Object.keys(contextVariables).length > 0) {
            setVariables(contextVariables);
        }
    }, [contextVariables]);

    // FAQ'leri kategori slug'ına göre fetch et
    useEffect(() => {
        // categorySlugFromUrl yoksa fetch yapma
        if (!categorySlugFromUrl) {
            return;
        }

        const abortController = new AbortController();
        const fetchFaqs = async () => {
            try {
                setLoading(true);
                setError(null);

                // API parametrelerini oluştur
                const params = new URLSearchParams();
                
                // Dil parametresini belirle
                let finalLanguage: string | undefined = undefined;
                
                if (resolvedLanguageCode) {
                    finalLanguage = resolvedLanguageCode;
                } else if (resolvedLanguageId) {
                    params.set('language_id', String(resolvedLanguageId));
                } else if (typeof window !== 'undefined') {
                    const pathParts = window.location.pathname.split('/').filter(Boolean);
                    const possibleLang = pathParts[0];
                    if (possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang)) {
                        finalLanguage = possibleLang;
                    } else {
                        const cookieLang = document.cookie
                            .split('; ')
                            .find(row => row.startsWith('language='))
                            ?.split('=')[1];
                        finalLanguage = cookieLang || undefined;
                    }
                }

                if (finalLanguage) {
                    params.set('language', finalLanguage);
                }

                // Kategori slug'ını ekle
                params.set('category_slug', categorySlugFromUrl);

                const response = await fetch(`/api/faqs?${params.toString()}`, {
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    throw new Error(`API error: ${response.status}`);
                }

                const data = await response.json();
                const faqData = data?.data || {};
                const apiFaqs: FaqEntry[] = faqData?.general_faqs || [];

                setGeneralFaqs(apiFaqs);
                setFetchedFaqs(buildFaqList(apiFaqs));
            } catch (err) {
                if (abortController.signal.aborted) {
                    return;
                }
                logError('[FaqCatPage] FAQs fetch error:', err);
                const errorMessage = err instanceof Error ? err.message : 'FAQ\'ler yüklenemedi';
                setError(errorMessage);
                setGeneralFaqs([]);
                setFetchedFaqs([]);
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchFaqs().catch(() => { /* Abort/unmount - unhandled rejection önlenir */ });
        return () => abortController.abort();
    }, [categorySlugFromUrl, resolvedLanguageCode, resolvedLanguageId, hasInitialData]);

    // FAQ listesini oluştur (gruplama yok)
    const faqsFromProps = useMemo(() => buildFaqList(generalFaqs), [generalFaqs]);
    // categorySlugFromUrl varsa ve fetch yapıldıysa fetchedFaqs kullan
    // Aksi halde props'tan gelen veriyi kullan
    const allFaqs = (categorySlugFromUrl && fetchedFaqs.length > 0) 
        ? fetchedFaqs 
        : faqsFromProps;

    const hasData = allFaqs.length > 0;

    // URL hash'ini oku ve ilgili soruyu aç
    useEffect(() => {
        if (!hasData || typeof window === 'undefined') {
            return;
        }

        const hash = window.location.hash;
        if (!hash || !hash.startsWith('#faq-')) {
            return;
        }

        // Hash'ten FAQ ID'sini çıkar (#faq-123 -> faq-123)
        const faqId = hash.substring(1); // #faq-123 -> faq-123
        
        // FAQ ID'sinin listede olup olmadığını kontrol et
        const faqExists = allFaqs.some(faq => faq.id === faqId);
        if (faqExists) {
            setOpenAccordion(faqId);
            
            // Scroll to element
            setTimeout(() => {
                const element = document.getElementById(`accordion-button-${faqId}`);
                if (element) {
                    element.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                    });
                }
            }, 300); // Accordion açılma animasyonu için kısa bir gecikme
        }
    }, [hasData, allFaqs]);
    // missingSelection: Kategori slug'ı yoksa, initial data yoksa ve enableClientFetch true ise
    // Ama categorySlugFromUrl varsa missingSelection false olmalı (URL'den geldiği için)
    const missingSelection = !categorySlugFromUrl && shouldFetch && !hasInitialData && enableClientFetch;

    // Tek sütunlu render (flex-col)
    const renderFaqs = (items: FAQItem[]) => {
        return (
            <div className="flex flex-col gap-6">
                {items.map((item) => (
                    <Accordion key={item.id} item={item} isOpen={openAccordion === item.id} onToggle={() => handleToggle(item.id)} />
                ))}
            </div>
        );
    };



    if (loading) {
        return (
            <section className="bg-white pt-[75px] pb-[60px]">
                <div className="container mx-auto py-20 text-center text-gray-500">
                    {getVariable('faqs_loading', 'Loading FAQs...')}
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="bg-white pt-[75px] pb-[60px]">
                <div className="container mx-auto py-20 text-center text-red-500">
                    {error}
                </div>
            </section>
        );
    }

    // missingSelection: Sadece client-side fetch durumunda ve hiç veri yoksa göster
    // Server-side'da sayfa ile ilişkili FAQ'ler otomatik gelir, bu yüzden bu kontrolü sadece client-side fetch için yap
    if (missingSelection && !hasInitialData) {
        return (
            <section className="bg-white pt-[75px] pb-[60px]">
                <div className="container mx-auto py-20 text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl">
                    {getVariable('faqs_no_selection', 'No FAQ selection made for this component. Please select the component in the page editor and use the "Select Questions" button.')}
                </div>
            </section>
        );
    }

    return (
        <motion.section
            className="bg-white pt-[75px] pb-[60px]"
            initial="hidden"
            animate="visible"
            variants={pageVariants}
        >
            <div className="container mx-auto">
                <div className="flex flex-col gap-10">

                    {!hasData && (
                        <motion.div
                            className="mt-12 text-center text-alsero-light-gray"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            {getVariable('faqs_no_faqs', 'No FAQs found for this language yet.')}
                        </motion.div>
                    )}

                    {hasData && (
                        <motion.div
                            className="mt-4"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {renderFaqs(allFaqs)}
                        </motion.div>
                    )}
                    
                </div>
            </div>
        </motion.section>
    )
}